/**
 * Export session telemetry data to a standardized CSV file.
 */

const BASE_DATA_KEYS = ['dist', 'time', 'lat', 'lng', 'speed', 'lateralG', 'longG'];

function formatLapTime(seconds) {
  if (seconds == null) return '';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.round((seconds % 1) * 1000);
  return `${mins}:${String(secs).padStart(2, '0')}:${String(ms).padStart(3, '0')}`;
}

function csvEscape(value) {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function discoverExtraKeys(laps) {
  const extra = new Set();
  for (const lap of laps) {
    if (!lap.data?.length) continue;
    // Sample first and last points of each lap to catch all fields
    const samples = [lap.data[0], lap.data[lap.data.length - 1]];
    for (const pt of samples) {
      for (const key of Object.keys(pt)) {
        if (!BASE_DATA_KEYS.includes(key)) extra.add(key);
      }
    }
  }
  return Array.from(extra).sort();
}

function buildFilename(session, sessionData) {
  const track = (sessionData?.track || session.track || 'Session').replace(/\s+/g, '_');
  const car = (sessionData?.car || session.car || '').replace(/\s+/g, '_');
  const date = sessionData?.date || session.date || '';
  const parts = [track, car, date].filter(Boolean);
  const name = parts.join('_').replace(/[^a-zA-Z0-9_\-]/g, '');
  return name + '.csv';
}

export function exportSessionCsv(session, sessionData) {
  if (!sessionData?.laps?.length) return;

  const extraKeys = discoverExtraKeys(sessionData.laps);
  const allDataKeys = [...BASE_DATA_KEYS, ...extraKeys];

  const track = sessionData.track || session.track || '';
  const car = sessionData.car || session.car || '';
  const date = sessionData.date || session.date || '';
  const deviceType = sessionData.deviceType || '';
  const sessionType = session.sessionType || '';
  const exportDate = new Date().toISOString().slice(0, 19).replace('T', ' ');

  // Session metadata header block
  const lines = [
    `# Exported from RaceVoice.com`,
    `# Export Date,${csvEscape(exportDate)}`,
    `# Track,${csvEscape(track)}`,
    `# Car,${csvEscape(car)}`,
    `# Date,${csvEscape(date)}`,
    `# DeviceType,${csvEscape(deviceType)}`,
    `# SessionType,${csvEscape(sessionType)}`,
    `#`,
  ];

  // Telemetry column headers
  const dataHeaders = ['LapNum', 'LapTime', ...allDataKeys.map(k => k.charAt(0).toUpperCase() + k.slice(1))];
  lines.push(dataHeaders.map(csvEscape).join(','));

  for (const lap of sessionData.laps) {
    for (const pt of (lap.data || [])) {
      const row = [lap.lapNum, formatLapTime(lap.lapTime), ...allDataKeys.map(k => csvEscape(pt[k]))];
      lines.push(row.join(','));
    }
  }

  const csvString = lines.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = buildFilename(session, sessionData);
  a.click();
  URL.revokeObjectURL(url);
}
